import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Card,
  FormDialog,
  MuiBox,
  MuiTable,
  SvgIcon,
  SvgIconButton,
  Tooltip,
  Typography
} from '@kis-technologies/kis-ui';
import { useActions } from 'hooks/useActions';
import { actions as ciActions, selectors as ciSelectors, ClientIntegration } from 'store/clientIntegrations';
import { getSortedList, getUniqueName } from 'utils/stringFormat';
import { useStyles } from '../../styled';
import { CreateEnvironmentOverlayProps, ManageEnvironmentsOverlayProps } from '../types';
import CreateEnvironmentOverlay from './CreateEnvironmentOverlay';

const ManageEnvironmentsOverlay = ({ onClose }: ManageEnvironmentsOverlayProps) => {
  const classes = useStyles();

  const { getClientIntegrations, createClientIntegration, deleteClientIntegration } = useActions({
    getClientIntegrations: ciActions.getClientIntegrations,
    createClientIntegration: ciActions.createClientIntegration,
    deleteClientIntegration: ciActions.deleteClientIntegration
  });
  const environments = useSelector(ciSelectors.selectClientIntegrations);
  const { pending } = useSelector(ciSelectors.selectAction);

  const [showCreateOverlay, setShowCreateOverlay] = useState(false);
  const [environmentToDelete, setEnvironmentToDelete] = useState<ClientIntegration | null>(null);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [uiLoading, setUILoading] = useState(false);

  const sortedEnvironments = useMemo(() => {
    const sorted = getSortedList(environments, 'name');
    return sortOrder === 'asc' ? sorted : [...sorted].reverse();
  }, [environments, sortOrder]);

  // Refresh the list once a create or delete request finishes. Errors are shown by EnvironmentsView.
  useEffect(() => {
    if (uiLoading && !pending) {
      setShowCreateOverlay(false);
      setUILoading(false);
      getClientIntegrations();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uiLoading, pending]);

  const onCreateSubmit: CreateEnvironmentOverlayProps['onSubmit'] = (payload) => {
    setUILoading(true);
    createClientIntegration(payload);
  };

  const onCopyClick = (environment: ClientIntegration) => {
    setUILoading(true);
    createClientIntegration({
      name: getUniqueName(environment.name, environments),
      baseUrl: environment.baseUrl,
      integrationId: environment.integration.id,
      configuration: environment.configuration,
      cityPass: environment.cityPass,
      souvenir: environment.souvenir
    });
  };

  const onDeleteConfirmClick = () => {
    if (environmentToDelete) {
      setUILoading(true);
      deleteClientIntegration({ id: environmentToDelete.id });
      setEnvironmentToDelete(null);
    }
  };

  return (
    <FormDialog
      open
      title="Environments"
      cancelButtonAction={onClose}
      confirmButtonText="Close"
      confirmButtonAction={onClose}
      headerButtonAction={() => setShowCreateOverlay(true)}
      headerButtonText="Create Environment"
    >
      <MuiBox py="16px">
        <Card hasBorderRadius raised fullHeight>
          <Box flexOverride="1">
            <MuiTable.TableContainer>
              <MuiTable.Table stickyHeader>
                <MuiTable.TableHead>
                  <MuiTable.TableRow>
                    <MuiTable.TableCell
                      className={classes.tableHeader}
                      sx={{ width: '440px', whiteSpace: 'nowrap', cursor: 'pointer' }}
                      onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                    >
                      Name
                      <SvgIcon
                        name={sortOrder === 'asc' ? 'drop_up' : 'drop_down'}
                        size={15}
                        style={{ marginLeft: '5px' }}
                      />
                    </MuiTable.TableCell>
                    <MuiTable.TableCell className={classes.tableHeader} sx={{ textAlign: 'center' }}>
                      Actions
                    </MuiTable.TableCell>
                  </MuiTable.TableRow>
                </MuiTable.TableHead>
                <MuiTable.TableBody>
                  {sortedEnvironments.map((environment: ClientIntegration) => (
                    <MuiTable.TableRow key={environment.id}>
                      <MuiTable.TableCell>{environment.name}</MuiTable.TableCell>
                      <MuiTable.TableCell align="center">
                        <MuiBox display="flex" gap="10px">
                          <Tooltip title="Copy Environment" arrow placement="top" verticalOffset={-8}>
                            <SvgIconButton
                              disabled={pending}
                              onClick={() => onCopyClick(environment)}
                              data-testid="copy-environment-button"
                            >
                              <SvgIcon name="copy_circle" />
                            </SvgIconButton>
                          </Tooltip>
                          <Tooltip title="Delete Environment" arrow placement="top" verticalOffset={-8}>
                            <SvgIconButton
                              disabled={pending}
                              onClick={() => setEnvironmentToDelete(environment)}
                              data-testid="delete-environment-button"
                            >
                              <SvgIcon name="exit_circle" />
                            </SvgIconButton>
                          </Tooltip>
                        </MuiBox>
                      </MuiTable.TableCell>
                    </MuiTable.TableRow>
                  ))}
                </MuiTable.TableBody>
              </MuiTable.Table>
            </MuiTable.TableContainer>
          </Box>
        </Card>
      </MuiBox>
      {environmentToDelete && (
        <FormDialog
          open
          confirmButtonAction={onDeleteConfirmClick}
          confirmButtonText="Delete"
          cancelButtonAction={() => setEnvironmentToDelete(null)}
          cancelButtonText="Cancel"
        >
          <Typography fontSize={24}>Are you sure you want to delete this environment?</Typography>
        </FormDialog>
      )}
      {showCreateOverlay && (
        <CreateEnvironmentOverlay onClose={() => setShowCreateOverlay(false)} onSubmit={onCreateSubmit} />
      )}
    </FormDialog>
  );
};

export default ManageEnvironmentsOverlay;
